/**
 * The quick add's pure parts: the draft, the form the Worker takes
 * (POST /api/todos, multipart so a screenshot rides along), and finding a
 * screenshot in a paste or a drop.
 */

import type { ItemKind } from '@satisa-memory/shared'

export const TODO_KINDS = [
  'i_owe',
  'owed_to_me',
  'follow_up',
  'decision'
] as const satisfies ItemKind[]

/** What the Worker stores; anything else is refused there too. */
export const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
export const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024

export interface TodoDraft {
  text: string
  kind: (typeof TODO_KINDS)[number]
  notes: string
  /** The counterpart first. */
  people: { id: string; name: string }[]
  /** A decision's date (when it takes effect), never a deadline: the Worker keeps both in one field. */
  dueDate: string
  screenshot: File | null
}

export function emptyDraft(): TodoDraft {
  return { text: '', kind: 'i_owe', notes: '', people: [], dueDate: '', screenshot: null }
}

/** The form fields, leaving out what is empty. */
export function todoForm(d: TodoDraft): {
  text: string
  kind: TodoDraft['kind']
  notes?: string
  people?: string
  dueDate?: string
  screenshot?: File
} {
  return {
    text: d.text.trim(),
    kind: d.kind,
    ...(d.notes.trim() ? { notes: d.notes.trim() } : {}),
    ...(d.people.length ? { people: d.people.map((p) => p.id).join(',') } : {}),
    ...(d.dueDate ? { dueDate: d.dueDate } : {}),
    ...(d.screenshot ? { screenshot: d.screenshot } : {})
  }
}

/** Why a file cannot be the screenshot, or null when it can. */
export function screenshotProblem(f: File): string | null {
  if (!SCREENSHOT_TYPES.includes(f.type)) return 'A screenshot must be a PNG, JPEG, GIF or WebP.'
  if (f.size > MAX_SCREENSHOT_BYTES) return 'A screenshot must be at most 10 MB.'
  return null
}

/** The first image in a paste or a drop. A pasted one gets a name, which it lacks. */
export function imageIn(data: DataTransfer | null | undefined): File | null {
  if (!data) return null
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind !== 'file' || !item.type.startsWith('image/')) continue
    const f = item.getAsFile()
    if (!f) continue
    if (f.name && f.name !== 'image.png') return f
    const ext = f.type.split('/')[1] ?? 'png'
    return new File([f], `screenshot.${ext}`, { type: f.type })
  }
  for (const f of Array.from(data.files ?? [])) if (f.type.startsWith('image/')) return f
  return null
}

/**
 * The ledger item a to-do's note belongs to, or null for any other link. The
 * note's URL is the item's own page (db/todos.ts), so that is where it opens:
 * as a link it would read like a document someone shared.
 */
export function todoItemId(link: {
  source: string
  kind: string | null
  url: string
}): string | null {
  if (link.source !== 'memory' || link.kind !== 'todo') return null
  try {
    return /^\/ledger\/items\/(itm_[a-z0-9]+)$/.exec(new URL(link.url).pathname)?.[1] ?? null
  } catch {
    return null
  }
}

/** Whether a key press is typing into something, so a shortcut must not fire. */
export function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))
}
